import { Star, Users, Clock, Check } from 'lucide-react';
import { useState, useEffect } from 'react';
import { imageUrl } from '../../utils/url';

function getPath(fullPath) {
  if (!fullPath) return null;
  const normalized = fullPath.replace(/\\/g, "/");
  const assetsIndex = normalized.indexOf("assets/");
  if (assetsIndex !== -1) {
    return normalized.substring(assetsIndex + 7);
  }
  const asstesIndex = normalized.indexOf("asstes/");
  if (asstesIndex !== -1) {
    return normalized.substring(asstesIndex + 7);
  }
  return normalized;
}

const getFullImageUrl = (imagePath) => {
  if (!imagePath) return null;
  
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  
  const extractedPath = getPath(imagePath);
  if (!extractedPath) return null;

  const cleanPath = extractedPath.replace(/^\/+/, '');
  return `${imageUrl}/${cleanPath}`;
};

const PreviewCard = ({ formData, onViewCurriculum }) => {
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
  const [imageError, setImageError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    console.log('PreviewCard - Received formData:', formData);
    
    // Clean up previous blob URL
    if (imagePreviewUrl && imagePreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(imagePreviewUrl);
    }
    
    setImageError(false);
    
    // Check if we have an image file (for live preview during editing)
    if (formData?.image && formData.image instanceof File) {
      const url = URL.createObjectURL(formData.image);
      setImagePreviewUrl(url);
      console.log('PreviewCard - Using uploaded file:', formData.image.name, url);
      return;
    }
    
    // Check for imgurl from existing course
    const imageUrlSource = formData?.imgurl;
    if (imageUrlSource && imageUrlSource !== '') {
      const fullUrl = getFullImageUrl(imageUrlSource);
      if (fullUrl) {
        setImagePreviewUrl(fullUrl);
        console.log('PreviewCard - Set image preview URL to:', fullUrl);
      } else {
        console.log('PreviewCard - Could not construct image URL from:', imageUrlSource);
        setImagePreviewUrl(null);
      }
    } 
    else {
      console.log('PreviewCard - No image URL or file available');
      setImagePreviewUrl(null);
    }
    
    // Cleanup function
    return () => {
      if (imagePreviewUrl && imagePreviewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(imagePreviewUrl);
      }
    };
  }, [formData?.image, formData?.imgurl, retryCount]);

  const handleImageError = () => {
    console.error('PreviewCard - Image failed to load:', imagePreviewUrl);
    setImageError(true);
    
    // Try one retry with a different path format if we have an imgurl
    const hasImageUrl = formData?.imgurl;
    if (retryCount === 0 && hasImageUrl) {
      console.log('PreviewCard - Retrying with different path format');
      setRetryCount(1);
      // Force re-evaluation of image URL
      const fullUrl = getFullImageUrl(formData.imgurl);
      if (fullUrl && fullUrl !== imagePreviewUrl) {
        setImagePreviewUrl(fullUrl);
      }
    }
  };

  const handleImageLoad = () => {
    console.log('PreviewCard - Image loaded successfully:', imagePreviewUrl);
    setImageError(false);
  };

  const getModuleTitle = (item) => {
    if (!item) return '';
    if (typeof item === 'string') return item;
    if (typeof item === 'object') return item.title || '';
    return '';
  };

  const getValidModules = () => {
    if (!formData?.syllabus || !Array.isArray(formData.syllabus)) {
      return ['Understanding E-Commerce Platforms', 'Documentation Requirements'];
    }
    
    return formData.syllabus
      .filter(item => {
        if (!item) return false;
        if (typeof item === 'string') return item.trim().length > 0;
        if (typeof item === 'object') return item.title && item.title.trim().length > 0;
        return false;
      })
      .map(item => getModuleTitle(item));
  };

  const validModules = getValidModules();
  const displayModules = validModules.slice(0, 2);
  const remainingCount = validModules.length - 2;
  const courseName = formData?.coursename || 'Sample Course Title';
  const courseDescription = formData?.description || 'This is a sample course description. Learn amazing skills and advance your career with our comprehensive syllabus.';
  const courseRating = formData?.rating || 4.9;
  const courseStudents = formData?.students || 8450;
  const courseDuration = formData?.duration || '12h30m';
  const courseLevel = formData?.levelname || formData?.level || 'Beginner';
  
  return (
    <div className="bg-white rounded-2xl w-full max-w-[380px] mx-auto overflow-hidden border border-gray-100 transition-all shadow-lg">
      <div className="relative h-[160px]">
        {imagePreviewUrl && !imageError ? (
          <img 
            src={imagePreviewUrl}
            alt={courseName}
            className="w-full h-full object-cover rounded-t-2xl"
            onError={handleImageError}
            onLoad={handleImageLoad}
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-orange-400 to-amber-500 rounded-t-2xl flex items-center justify-center">
            <div className="text-center text-white">
              <svg className="w-8 h-8 mx-auto mb-1 opacity-70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span className="text-xs">{imageError ? 'Failed to load' : 'No image'}</span>
            </div>
          </div>
        )}
        
        <div className="absolute top-3 right-3 bg-gradient-to-r from-amber-500 to-orange-500 px-3 rounded-md">
          <span className="text-xs font-semibold text-white tracking-wide">Offer</span>
        </div>
        
        <div className="absolute bottom-3 left-3">
          <span className="text-sm font-medium text-white drop-shadow-md">{courseLevel}</span>
        </div>
      </div>
      
      <div className="p-5 pt-6">
        <h3 className="text-lg font-bold text-gray-900 leading-tight mb-2 line-clamp-2">{courseName}</h3>
        <p className="text-sm text-gray-500 leading-relaxed mb-4 line-clamp-2">{courseDescription}</p>
        
        <button className="cursor-pointer w-full bg-gradient-to-r from-amber-500 to-orange-500 text-white py-2.5 rounded-xl font-semibold text-sm mb-5 hover:shadow-lg transition-all transform hover:scale-[1.02]">
          Enroll Now
        </button>
        
        <div className="flex items-center justify-between py-3 border-t border-b border-gray-100 mb-5">
          <div className="flex items-center gap-1.5">
            <div className="flex gap-0.5">
              {[...Array(5)].map((_, i) => (
                <Star 
                  key={i} 
                  size={12} 
                  className={i < Math.floor(courseRating) ? 'fill-orange-500 text-orange-500' : 'fill-gray-200 text-gray-200'} 
                />
              ))}
            </div>
            <span className="text-sm font-bold text-gray-800">{courseRating}</span>
          </div>
          
          <div className="flex items-center gap-1.5">
            <Users size={12} className="text-gray-400" />
            <span className="text-sm font-semibold text-gray-800">
              {typeof courseStudents === 'number' ? courseStudents.toLocaleString() : courseStudents}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <Clock size={12} className="text-gray-400" />
            <span className="text-sm font-semibold text-gray-800">{courseDuration}</span>
          </div>
        </div>
        
        <div>
          <ul className="space-y-2">
            {displayModules.map((item, idx) => (
              <li key={idx} className="flex items-start gap-1.5">
                <div className="w-4 h-4 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check size={8} className="text-green-600 font-bold" />
                </div>
                <span className="text-[11px] text-gray-600 leading-tight line-clamp-2">
                  {item}
                </span>
              </li>
            ))}
            {remainingCount > 0 && (
              <li 
                className="text-[10px] font-semibold text-orange-500 ml-5 cursor-pointer hover:underline"
                onClick={(e) => {
                  e.stopPropagation();
                  if (formData?.syllabus && formData.syllabus.length > 0) {
                    onViewCurriculum && onViewCurriculum(formData);
                  }
                }}
              >
                +{remainingCount} more module{remainingCount > 1 ? 's' : ''}
              </li>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default PreviewCard;